import { NgModule } from '@angular/core';
import { HttpClientModule } from '@angular/common/http';
import { APOLLO_OPTIONS, ApolloModule } from 'apollo-angular';
import { HttpLink, HttpLinkModule } from 'apollo-angular-link-http';
import { InMemoryCache } from 'apollo-cache-inmemory';
import { ApolloLink, split } from 'apollo-link';
import { setContext } from 'apollo-link-context';
import { environment } from '../environments/environment';
import { GC_AUTH_TOKEN, GC_EXPIRE, GC_EXPIRE_7DAYS } from './constants';
import { SessionService } from './global/services/session.service';
import * as moment from 'moment';
import { LoginSuccessModel } from './auth/models/login-success.model';
import { DefaultOptions } from 'apollo-client';
import { WebSocketLink } from 'apollo-link-ws';
import { getMainDefinition } from 'apollo-utilities';

const uri = environment.BASE_URL + '/api';

const defaultOptions: DefaultOptions = {
  watchQuery: {
    fetchPolicy: 'no-cache',
    errorPolicy: 'ignore'
  },
  query: {
    fetchPolicy: 'no-cache',
    errorPolicy: 'all'
  }
};

export function provideApollo(httpLink: HttpLink) {
  const basic = setContext((operation, context) => ({
    headers: {
      Accept: 'charset=utf-8'
    }
  }));

  // Get the authentication token from local storage if it exists
  const auth = setContext(async (_, { headers }) => {
    const token = localStorage.getItem(GC_AUTH_TOKEN);
    const tokenTime = localStorage.getItem(GC_EXPIRE);

    if (token && tokenTime) {
      const localTime = moment.utc(tokenTime).local();
      const difference = localTime.diff(moment(), 'minutes');

      if (difference < 10) {
        const tokenResp = await fetch(
          environment.BASE_URL + '/auth/refresh_token',
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        const tokenData: LoginSuccessModel = await tokenResp.json();

        if (tokenData) {
          localStorage.setItem(GC_EXPIRE, tokenData.expire);
          localStorage.setItem(
            GC_EXPIRE_7DAYS,
            moment(tokenData.expire)
              .add(7, 'days')
              .toString()
          );
          /**
           * will expire in 1 week hence wont be found
           * */
          localStorage.setItem(GC_AUTH_TOKEN, tokenData.token);
        }
      }
    }

    const updatedToken = localStorage.getItem(GC_AUTH_TOKEN);

    return {
      headers: {
        Authorization: `Bearer ${updatedToken}`
      }
    };
  });

  const httpUrlLInk = httpLink.create({ uri });

  const webSocketLink = new WebSocketLink({
    uri: environment.WEBSOCKET_URL + '/api',
    options: {
      reconnect: true,
      lazy: true,
      connectionParams: {
        Authorization: `Bearer ${localStorage.getItem(GC_AUTH_TOKEN)}`
      }
    }
  });

  const hasSubscriptionOperation = ({ query: { definitions } }: any) =>
    definitions.some(
      ({ kind, operation }: { kind: string; operation: string }) =>
        kind === 'OperationDefinition' && operation === 'subscription'
    );

  const enhanced_link = split(
    // split based on operation type
    ({ query }) => {
      const dif = getMainDefinition(query);
      return (
        dif.kind === 'OperationDefinition' && dif.operation === 'subscription'
      );
    },
    webSocketLink,
    httpUrlLInk
  );

  // const link = ApolloLink.from([basic, auth, httpUrlLInk]);
  const link = ApolloLink.from([basic, auth, enhanced_link]);

  const cache = new InMemoryCache();

  return {
    link,
    cache,
    defaultOptions
  };
}

@NgModule({
  exports: [HttpClientModule, ApolloModule, HttpLinkModule],
  providers: [
    {
      provide: APOLLO_OPTIONS,
      useFactory: provideApollo,
      deps: [HttpLink]
    }
  ]
})
export class GraphqlModule {
  constructor(public sessionService: SessionService) {}
}
